import React, { FormEvent, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import Add from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import Spinner from '~/components/Common/Spinner';

import {
  AVAILABLE_REGIONS_LIB_REGIONS,
  COMMISSION_RULE_CONDITION_TYPES,
  CommissionsConditionTypes,
  INITIAL_COMMISSION_CONDITIONS,
  PRODUCT_TYPES_OPTIONS,
  RuleCondition,
  RuleConditions,
} from '~/consts/agentHub';

import { useCreateCommission } from '~/hooks/apis/agent/useCreateCommission';
import { useGetAgenciesById } from '~/hooks/apis/agent/useGetAgenciesById';
import { useGetUsersSummaryByIds } from '~/hooks/apis/agent/useGetUsersSummaryByIds';
import { useUpdateCommission } from '~/hooks/apis/agent/useUpdateCommission';
import useCurrentBrand from '~/hooks/useCurrentBrand';
import useUserBreadcrumbs from '~/hooks/useUserBreadcrumbs';

import { Dayjs, formatDateLongISO, formatDateSlashes, toDayJs } from '~/services/TimeService';

import { Commission } from '~/types/services/agentHub';

import { arrayToObject } from '~/utils/arrayUtils';
import { buildRuleParam, groupByValue } from '~/utils/commission';
import { capitalise } from '~/utils/stringUtils';

import { AddNewConditionForm } from './AddNewConditionForm';

interface Props {
  data?: Commission;
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
}

type InputOptionFunc = (data) => { value: string; label: string };

const SHRUNK_LABEL = {
  textField: {
    InputLabelProps: { shrink: true },
  },
};

const INPUT_BORDER = {
  sx: {
    borderRadius: 0.5,
  },
};

export default function CommissionDialog({ data, onClose, isOpen, mode }: Props) {
  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const textTransformNone = { textTransform: 'none' };

  const [createdConditions, setCreatedConditions] = useState<RuleConditions>(INITIAL_COMMISSION_CONDITIONS);
  const [regions, setRegions] = useState<Array<string>>([]);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const commissionFormRef = useRef<HTMLFormElement>(null);
  const [visibleConditions, setVisibleConditions] = useState<Set<CommissionsConditionTypes>>(new Set());
  const [shouldIncludeLimit, setShouldIncludeLimit] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();
  const brand = useCurrentBrand();

  const createCommissionMutation = useCreateCommission({
    onSuccess: () => {
      enqueueSnackbar('Commission rule created', { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(error?.message || 'Unknown error', { variant: 'error' });
    },
  });
  const updateCommissionMutation = useUpdateCommission({
    onSuccess: () => {
      enqueueSnackbar('Commission rule updated', { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(error?.message || 'Unknown error', { variant: 'error' });
    },
  });

  const groupedProductedTypes = useMemo(() => {
    return arrayToObject(PRODUCT_TYPES_OPTIONS, (productType) => productType);
  }, []);
  const totalConditions = useMemo(() => visibleConditions.size, [visibleConditions]);
  const remainingConditions = COMMISSION_RULE_CONDITION_TYPES.filter((cond) => !visibleConditions.has(cond));
  const getRuleTypeFunction = useCallback(
    (type: string): InputOptionFunc => {
      switch (type) {
        case 'productType':
          return (data) => ({ value: data, label: groupedProductedTypes[data] });
        default:
          return (data) => ({ value: data, label: data });
      }
    },
    [groupedProductedTypes],
  );

  const agencyIds = useMemo(() => {
    if (!data?.rules?.agency || !isOpen) {
      return [];
    }
    return data.rules.agency;
  }, [data?.rules?.agency, isOpen]);

  const agentIds = useMemo(() => {
    if (!data?.rules?.agent || !isOpen) {
      return [];
    }
    return data.rules.agent;
  }, [data?.rules?.agent, isOpen]);

  const { data: agenciesData, isFetching: isLoadingAgencies } = useGetAgenciesById(agencyIds);
  const { data: agentsMap, isFetching: isLoadingAgents } = useGetUsersSummaryByIds(agentIds, brand);
  const { data: creator, isLoading: isLoadingCreator } = useUserBreadcrumbs({
    userId: isEdit && data?.createdBy,
    enabled: Boolean(isEdit && data?.createdBy),
  });
  const { data: updater, isLoading: isLoadingUpdater } = useUserBreadcrumbs({
    userId: isEdit && data?.updatedBy,
    enabled: Boolean(isEdit && data?.updatedBy),
  });

  const isLoadingCommissionEditHistory = isLoadingCreator || isLoadingUpdater;
  const isLoadingConditions = isLoadingAgencies || isLoadingAgents;

  const resetForm = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    setRegions([]);
    setCreatedConditions(INITIAL_COMMISSION_CONDITIONS);
    setVisibleConditions(new Set());
    setShouldIncludeLimit(false);
    commissionFormRef.current?.reset();
  }, []);

  useEffect(() => {
    if (!data || !isOpen) {
      return;
    }
    setRegions(data.regions);
    if (data.startDate) {
      setStartDate(toDayJs(data.startDate));
    }

    if (data.endDate) {
      setEndDate(toDayJs(data.endDate));
    } else {
      setEndDate(null);
    }
    setShouldIncludeLimit(data.limit != null);

    const conditionsObject: RuleConditions = arrayToObject(
      Object.entries(data.rules),
      ([key]) => key as keyof RuleConditions,
      ([key, rules]) => (rules as Array<string>).map((rule) => getRuleTypeFunction(key)(rule)),
    );

    if (agenciesData && agencyIds.length > 0) {
      conditionsObject.agency = agenciesData.map((agency) => ({ value: agency.id, label: agency.name }));
    }

    if (agentsMap && agentIds.length > 0) {
      const agentsArray = Array.from(agentsMap.values());
      conditionsObject.agent = agentsArray.map((agent: { id_member: string; full_name: string; email: string }) => ({
        value: agent.id_member,
        label: `${agent.full_name} [${agent.email}]`,
      }));
    }

    setCreatedConditions(conditionsObject);
    setVisibleConditions(new Set(Object.keys(conditionsObject) as Array<CommissionsConditionTypes>));

    return () => {
      resetForm();
    };
  }, [data, isOpen, getRuleTypeFunction, agenciesData, agentsMap, agencyIds.length, agentIds.length, resetForm]);

  function addCondition(newCondition: RuleCondition, replace = false) {
    setCreatedConditions((createdConditions) => {
      const conditions = createdConditions[newCondition.type];
      if (!conditions) {
        return { ...createdConditions, [newCondition.type]: [...newCondition.value] };
      }
      const currentConditions = replace ? [] : conditions.reduce(groupByValue, new Map());
      const newConditions = newCondition.value.reduce(groupByValue, new Map());
      const mergedConditions = Array.from(new Map([...currentConditions, ...newConditions]).entries());
      const newConditionsForType = mergedConditions.map(([value, label]) => ({ value, label }));
      return {
        ...createdConditions,
        [newCondition.type]: newConditionsForType,
      };
    });
  }

  function handleChangeCondition(prevType: CommissionsConditionTypes, type: CommissionsConditionTypes) {
    setCreatedConditions((createdConditions) => {
      return {
        ...createdConditions,
        [type]: [],
        [prevType]: [],
      };
    });
    setVisibleConditions((setConditions) => {
      setConditions.delete(prevType);
      setConditions.add(type);
      return new Set(setConditions);
    });
  }

  function handleClearCondition(type: string) {
    setCreatedConditions((createdConditions) => {
      return {
        ...createdConditions,
        [type]: [],
      };
    });
    setVisibleConditions((setConditions) => {
      setConditions.delete(type as CommissionsConditionTypes);
      return new Set(setConditions);
    });
  }

  function handleAppendCondition(type: CommissionsConditionTypes) {
    setCreatedConditions((createdConditions) => {
      return {
        ...createdConditions,
        [type]: [],
      };
    });
    setVisibleConditions((setConditions) => {
      setConditions.add(type);
      return new Set(setConditions);
    });
  }

  function handleStartDateChange(date: Dayjs) {
    setStartDate(date);
  }

  function handleEndDateChange(date: Dayjs) {
    setEndDate(date);
  }

  async function handleSaveCommission(event: FormEvent<HTMLFormElement>, id: string, conditions: RuleConditions) {
    event.preventDefault();

    const isConditionsEmpty = Object.values(createdConditions).every((condition) => condition.length === 0);

    if (isConditionsEmpty) {
      enqueueSnackbar('Please add at least one condition', { variant: 'error' });
      return;
    }

    const formData = new FormData(event.currentTarget);
    const regionsForm = formData?.get('regions') as string;
    const regionsValue = regionsForm === '' ? [] : regionsForm.split(',');
    const limitValue = formData.get('limit') as string;
    const formObj = {
      rules: buildRuleParam(conditions),
      description: formData.get('description') as string,
      startDate: startDate ? formatDateLongISO(startDate.toDate()) : formatDateLongISO(new Date()),
      endDate: endDate ? formatDateLongISO(endDate.toDate()) : undefined,
      regions: regionsValue,
      discountType: formData.get('discountType') as string,
      discountAmount: parseInt(formData.get('discountAmount') as string),
      commissionType: formData.get('discountType') as string,
      commissionPercentage: parseInt(formData.get('discountAmount') as string),
      type: formData.get('discountType') as string,
      limit: shouldIncludeLimit ? parseInt(limitValue) : null,
    };

    const onSuccess = () => {
      const hasChangedLimit =
        (data?.limit && Number(limitValue) !== data?.limit) || (!data?.limit && Number(limitValue) !== 0);
      if (hasChangedLimit) {
        enqueueSnackbar('Limit updated, this may take a few minutes to take effect', { variant: 'warning' });
      }
      resetForm();
      onClose();
    };

    if (isCreate) {
      createCommissionMutation.mutate(formObj, { onSuccess });
    } else {
      updateCommissionMutation.mutate({ commissionId: data?.id, data: formObj }, { onSuccess });
    }
  }

  function onCloseModal() {
    resetForm();
    onClose();
  }

  const isSaving = createCommissionMutation.isPending || updateCommissionMutation.isPending;

  return (
    <Dialog open={isOpen} fullWidth onClose={onCloseModal}>
      <DialogTitle>{isCreate ? 'Create commission rule' : `Edit commission rule "${data?.description}"`}</DialogTitle>
      <form ref={commissionFormRef} onSubmit={(event) => handleSaveCommission(event, data?.id, createdConditions)}>
        <Stack padding={2} paddingTop={0} direction="column" spacing={2}>
          {data?.hasReachedLimit && (
            <Alert severity="warning">
              This commission rule has reached its limit of usage and is no longer active. If you change it, it can
              accidentally become active again.
            </Alert>
          )}
          <Stack direction="row" gap={2}>
            <TextField
              placeholder="e.g. Flight Center UK - Hotel + Tours"
              name="description"
              defaultValue={data?.description}
              label="Description"
              required
              fullWidth
              InputLabelProps={SHRUNK_LABEL.textField.InputLabelProps}
              InputProps={INPUT_BORDER}
            />
          </Stack>
          <Stack direction="row" gap={2} justifyContent="space-between">
            <FormControl fullWidth>
              <InputLabel
                sx={{ backgroundColor: 'white', padding: '0 4px' }}
                shrink
                id="agent-hub-commissions-discount-type-input-label"
              >
                Commission type
              </InputLabel>
              <Select
                defaultValue={data?.type}
                displayEmpty
                name="discountType"
                renderValue={(value) => {
                  if (!value) {
                    return <Typography color="#7E8188BB">Select commission type</Typography>;
                  }
                  return capitalise(value);
                }}
                label="Commission type"
                required
                fullWidth
                sx={INPUT_BORDER.sx}
              >
                <MenuItem value="base">Base</MenuItem>
                <MenuItem value="additional">Additional</MenuItem>
              </Select>
            </FormControl>
            <TextField
              name="discountAmount"
              InputProps={{ inputProps: { min: 1, max: 100 }, ...INPUT_BORDER }}
              label="Commission percentage"
              defaultValue={data?.commissionPercentage}
              type="number"
              required
              fullWidth
              InputLabelProps={SHRUNK_LABEL.textField.InputLabelProps}
              placeholder="e.g. 8%"
            />
          </Stack>

          <Stack direction="row" gap={2}>
            <Stack flex={1}>
              <DatePicker
                value={startDate}
                onChange={handleStartDateChange}
                format="DD/MM/YYYY"
                name="startDate"
                label="Start date"
                sx={INPUT_BORDER.sx}
                slotProps={{ textField: { ...SHRUNK_LABEL.textField, InputProps: INPUT_BORDER } }}
              />
            </Stack>
            <Stack flex={1}>
              <DatePicker
                value={endDate}
                onChange={handleEndDateChange}
                format="DD/MM/YYYY"
                disablePast
                name="endDate"
                label="End date"
                slotProps={{ textField: { ...SHRUNK_LABEL.textField, InputProps: INPUT_BORDER } }}
              />
            </Stack>
          </Stack>
          <Stack direction="row" gap={2}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel
                sx={{ backgroundColor: 'white', padding: '0 4px' }}
                shrink
                id="agent-hub-commissions-regions-input-label"
              >
                Regions
              </InputLabel>
              <Select
                displayEmpty
                name="regions"
                labelId="agent-hub-commissions-regions-input-label"
                multiple
                value={regions}
                input={<OutlinedInput label="Regions" />}
                sx={INPUT_BORDER.sx}
                renderValue={(selected: Array<string>) => {
                  if (selected.length === 0) {
                    return <Typography color="#7E8188BB">Select regions</Typography>;
                  }
                  return selected.join(', ');
                }}
                onChange={(event) => {
                  setRegions(event.target.value as Array<string>);
                }}
              >
                {AVAILABLE_REGIONS_LIB_REGIONS.map((region) => (
                  <MenuItem key={region.code} value={region.code}>
                    <Checkbox checked={regions.includes(region.code)} />
                    <ListItemText primary={region.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack flex={1} spacing={1}>
              <TextField
                name="limit"
                InputProps={{ inputProps: { min: 0 }, ...INPUT_BORDER }}
                label="Limit (of usage)"
                defaultValue={data?.limit}
                type="number"
                fullWidth
                disabled={!shouldIncludeLimit}
                InputLabelProps={SHRUNK_LABEL.textField.InputLabelProps}
                placeholder="e.g. 5"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shouldIncludeLimit}
                    onChange={(e) => setShouldIncludeLimit(e.target.checked)}
                    size="small"
                  />
                }
                label="Set limit to commission"
              />
            </Stack>
          </Stack>
          <Divider />

          <Stack spacing={2}>
            <Typography variant="button" color="secondary">
              Conditions:
            </Typography>

            {isLoadingConditions && <Spinner size={14} />}
            <Stack direction="column" spacing={3}>
              {Array.from(visibleConditions).map((key) => {
                const value = createdConditions[key];
                return (
                  <Fragment key={key}>
                    <Stack justifyContent="space-between" key={key} direction="row" spacing={2}>
                      <Box flex={1}>
                        <AddNewConditionForm
                          conditionType={key}
                          ruleConditions={value}
                          addCondition={addCondition}
                          visibleConditions={visibleConditions}
                          handleChangeCondition={handleChangeCondition}
                        />
                      </Box>
                      <DeleteIcon onClick={() => handleClearCondition(key)} />
                    </Stack>
                    <Divider />
                  </Fragment>
                );
              })}
            </Stack>
          </Stack>

          {totalConditions < COMMISSION_RULE_CONDITION_TYPES.length && (
            <Stack direction="row" justifyContent="flex-start" spacing={2}>
              <Button
                size="small"
                variant="text"
                sx={textTransformNone}
                onClick={() => {
                  handleAppendCondition(remainingConditions[0]);
                }}
              >
                <Add /> Add condition
              </Button>
            </Stack>
          )}

          {isLoadingCommissionEditHistory && <Spinner size={14} />}

          {creator && (
            <Stack>
              <Typography variant="caption" color="secondary">
                Created By: <Link href={`/users/${creator?.user.href}`}>{creator?.user.label}</Link> at{' '}
                {formatDateSlashes(data?.createdAt)}
              </Typography>
              {updater && (
                <Typography variant="caption" color="secondary">
                  Edited By: <Link href={`/users/${updater?.user.href}`}>{updater?.user.label}</Link> at{' '}
                  {formatDateSlashes(data?.updatedAt)}
                </Typography>
              )}
            </Stack>
          )}

          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button sx={textTransformNone} type="button" size="medium" variant="text" onClick={onCloseModal}>
              Cancel
            </Button>
            <Button sx={textTransformNone} type="submit" size="medium" variant="contained" disabled={isSaving}>
              {isSaving ? 'Saving...' : isCreate ? 'Create rule' : 'Save changes'}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Dialog>
  );
}
